import React, { useCallback, useState, useEffect, useRef } from "react";
import { Offcanvas } from "react-bootstrap";
import {
    Flex,
    View,
    DialogContainer,
    AlertDialog,
    Link,
} from "@adobe/react-spectrum";
import {
    AtollonButton,
    AtollonButtonDesignType,
} from "../../../../components/ui/Button/AtollonButton";
import { useTranslation } from "react-i18next";
import { CustomView } from "../../../../services/soap/dataObjects/CustomView";
import {
    CustomViewDialog,
    ICustomViewData,
    transformNamesToName,
    buildDefinition,
} from "../../../../components/protected/Dialogs/CustomViewDialog";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state/store";
import styles from "./filter_component.module.css";
import { SettingItem } from "../../../../services/soap/features/responses/GetFormSettingsResponse";
import {
    EditIcon,
    TrashIcon,
    CopyIcon,
} from "../../../../components/protected/Icons/IconsLib";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { DeleteCustomViewsRequest } from "../../../../services/soap/features/requests/DeleteCustomViewsRequest";
import { DeviceInfo } from "../types";
import Building from "@spectrum-icons/workflow/Building";
import User from "@spectrum-icons/workflow/User";
import UserGroup from "@spectrum-icons/workflow/UserGroup";
import Alert from "@spectrum-icons/workflow/Alert";
import Rail from "@spectrum-icons/workflow/Rail";
import { UpdateCustomViewsRequest } from "../../../../services/soap/features/requests/UpdateCustomViewsRequest";

import clsx from "clsx";

const CURRENT_USER_INTENT_TOKEN = "__CURRENT_USER__";

export interface IFilterViewsComponentProps {
    views: CustomView[];
    selectedViewId?: string;
    onViewSelect: (view: CustomView | null) => void;
    onCreateView?: () => void;
    onLoadMore?: () => void;
    onViewsRefresh?: () => void;
    hasMore?: boolean;
    isMobile?: boolean;
    show?: boolean;
    onClose?: () => void;
    onShow?: () => void;
    store?: any;
    createParams?: (filterTypeName: any, filterValue: any) => void;
    formSettings?: SettingItem[];
    deviceInfo?: DeviceInfo;
    filterParams?: any;
    getPersistedFiltersRef?: React.MutableRefObject<
        ((viewId: string) => any[] | null) | null
    >;
    resetBaselineRef?: React.MutableRefObject<(() => void) | null>;
}

const FilterViewsComponent: React.FC<IFilterViewsComponentProps> = ({
    views,
    selectedViewId,
    onViewSelect,
    onCreateView,
    onLoadMore,
    onViewsRefresh,
    hasMore = false,
    isMobile = false,
    show,
    onClose,
    onShow,
    store,
    createParams,
    formSettings,
    deviceInfo,
    filterParams,
    getPersistedFiltersRef,
    resetBaselineRef,
}) => {
    const { t } = useTranslation();
    const { featureService, store: depStore } = useDependency();
    const selectedAppId = useSelector(
        (state: RootState) => state.userWebLayout.selectedAppId
    );
    const [displayCount, setDisplayCount] = useState(5);
    const [isViewsExpanded, setIsViewsExpanded] = useState(false);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingView, setEditingView] = useState<CustomView | null>(null);
    const [viewToDelete, setViewToDelete] = useState<CustomView | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [duplicateView, setDuplicateView] = useState<CustomView | null>(null);
    const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
    const pendingReselectViewIdRef = useRef<string | null>(null);
    const [reorderedViews, setReorderedViews] = useState<CustomView[]>(views);
    const [draggedKey, setDraggedKey] = useState<string | null>(null);
    const [dragOverKey, setDragOverKey] = useState<string | null>(null);
    const [hasFilterChanges, setHasFilterChanges] = useState(false);
    const initialViewsOrderRef = useRef<string[]>([]);
    const initialFilterStateRef = useRef<Record<string, any>>({});
    // Track the last selectedViewId for which we captured initial state
    const lastCapturedViewIdRef = useRef<string | null>(null);
    // Store latest filterParams in ref to access in timeout callback
    const filterParamsRef = useRef<any>(filterParams);
    // Store filter overrides in React state (runtime persistence)
    const filterOverridesRef = useRef<Record<string, any[]>>({});
    // Track when a view is being applied to prevent false change detection
    const isApplyingViewRef = useRef<boolean>(false);
    // Track timeout for baseline capture
    const baselineCaptureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Track previous filterParams to detect stabilization
    const prevFilterParamsForStabilizationRef = useRef<any>(null);
    // Track if drop already occurred to prevent double call from onDrop and onDragEnd
    const dropOccurredRef = useRef<boolean>(false);
    // Touch drag tracking for mobile/tablet
    const touchDragRef = useRef<{
        viewId: string;
        startY: number;
        currentY: number;
    } | null>(null);
    const isTouchDraggingRef = useRef<boolean>(false);

    const normalizeFilterName = useCallback((name?: string): string | null => {
        if (!name) return null;
        if (!name.includes("|")) return name.trim();
        const parts = name.split("|").map((p) => p.trim());
        const index = ["dimension1", "dimension2", "dimension3"].includes(parts[0])
            ? 1
            : 0;
        return parts[index]?.trim() || name.trim();
    }, []);

    const getIntentCurrentUserFilterNames = useCallback(
        (view: CustomView | null | undefined): Set<string> => {
            const set = new Set<string>();
            if (!view?.definition) return set;
            try {
                const parsed = JSON.parse(view.definition) as any;
                if (!Array.isArray(parsed?.filters)) return set;

                parsed.filters.forEach((f: any) => {
                    const filterInfo = Array.isArray(f?.filterInfo)
                        ? f.filterInfo
                        : [];
                    const hasCurrentUserFlag = filterInfo.some(
                        (fi: any) => fi?.isCurrentUser === true
                    );
                    if (!hasCurrentUserFlag) return;

                    const rawName: string | undefined =
                        f?.name ?? f?.itemname ?? undefined;
                    const normalized = normalizeFilterName(rawName);
                    if (normalized) set.add(normalized);
                });
            } catch (error) {
                console.error("Error parsing custom view definition:", error);
            }
            return set;
        },
        [normalizeFilterName]
    );

    const normalizeValueForIntentComparison = useCallback(
        (filterName: string, rawValue: any, intentCurrentUserNames: Set<string>) => {
            const value = String(rawValue ?? "").trim();
            if (!intentCurrentUserNames.has(filterName)) return value;

            const sessionUserId = String(depStore?.UserId ?? "").trim();
            if (sessionUserId && value === sessionUserId) {
                return CURRENT_USER_INTENT_TOKEN;
            }
            return value;
        },
        [depStore?.UserId]
    );

    // Sync reorderedViews with views prop changes and sort by priority
    useEffect(() => {
        // Sort views by priority: organization (0-1000), profile (1001-10000), user (10000+)
        const sortedViews = [...views].sort((a, b) => {
            const priorityA = a.priority ? parseInt(a.priority, 10) : 999999;
            const priorityB = b.priority ? parseInt(b.priority, 10) : 999999;
            return priorityA - priorityB;
        });

        setReorderedViews(sortedViews);
        // Store initial order for comparison
        initialViewsOrderRef.current = sortedViews.map((v) => v.id || "");
    }, [views]);

    // Keep filterParamsRef in sync with filterParams
    useEffect(() => {
        filterParamsRef.current = filterParams;
    }, [filterParams]);

    // Mark when a view is being applied (selectedViewId changes)
    useEffect(() => {
        if (selectedViewId && selectedViewId !== lastCapturedViewIdRef.current) {
            isApplyingViewRef.current = true;
            // Clear any existing timeout
            if (baselineCaptureTimeoutRef.current) {
                clearTimeout(baselineCaptureTimeoutRef.current);
                baselineCaptureTimeoutRef.current = null;
            }
            // Reset previous filterParams for stabilization check
            prevFilterParamsForStabilizationRef.current = null;
        } else if (!selectedViewId) {
            // View deselected
            isApplyingViewRef.current = false;
            initialFilterStateRef.current = {};
            lastCapturedViewIdRef.current = null;
            if (baselineCaptureTimeoutRef.current) {
                clearTimeout(baselineCaptureTimeoutRef.current);
                baselineCaptureTimeoutRef.current = null;
            }
        }
    }, [selectedViewId]);

    // Store initial filter state when view is selected and stabilized
    // IMPORTANT: Capture from filterParams AFTER view is applied and stabilized, not from definition
    // This ensures we capture the actual applied state (with runtime transformations)
    // rather than the design-time definition which may differ
    // We wait for filterParams to stabilize (no changes for a period) before capturing baseline
    useEffect(() => {
        if (!selectedViewId || !filterParams || !isApplyingViewRef.current) {
            return;
        }

        // Only capture initial state once per view selection
        // If we've already captured for this viewId, skip (unless view changed)
        if (lastCapturedViewIdRef.current === selectedViewId) {
            isApplyingViewRef.current = false;
            return;
        }

        const selectedView = reorderedViews.find(
            (v) => v.id === selectedViewId
        );
        if (!selectedView) {
            initialFilterStateRef.current = {};
            lastCapturedViewIdRef.current = null;
            isApplyingViewRef.current = false;
            return;
        }

        // Check if filterParams has the correct "state" filter matching selectedViewId
        const stateFilter = filterParams.FILTER?.COL?.find(
            (f: any) => f.name === "state"
        );
        if (!stateFilter || String(stateFilter.value) !== selectedViewId) {
            // View not yet applied, wait for it
            return;
        }

        // Check if filterParams has stabilized (same as previous check)
        const currentParamsString = JSON.stringify(filterParams.FILTER?.COL || []);
        const prevParamsString = prevFilterParamsForStabilizationRef.current
            ? JSON.stringify(prevFilterParamsForStabilizationRef.current)
            : null;

        if (prevParamsString !== currentParamsString) {
            // Still changing, update previous and wait
            prevFilterParamsForStabilizationRef.current = filterParams.FILTER?.COL || [];
            
            // Clear existing timeout and set new one
            if (baselineCaptureTimeoutRef.current) {
                clearTimeout(baselineCaptureTimeoutRef.current);
            }
            
            // Wait for stabilization (no changes for 200ms)
            baselineCaptureTimeoutRef.current = setTimeout(() => {
                // Re-check that filterParams hasn't changed during timeout
                const latestFilterParams = filterParamsRef.current;
                if (!latestFilterParams) {
                    isApplyingViewRef.current = false;
                    return;
                }

                const latestStateFilter = latestFilterParams.FILTER?.COL?.find(
                    (f: any) => f.name === "state"
                );
                if (!latestStateFilter || String(latestStateFilter.value) !== selectedViewId) {
                    isApplyingViewRef.current = false;
                    return;
                }

                try {
                    const intentCurrentUserNames =
                        getIntentCurrentUserFilterNames(selectedView);
                    // Capture initial state from ACTUAL filterParams (after view is applied and stabilized)
                    // This ensures we capture the runtime state with all transformations applied
                    const currentFilters = latestFilterParams.FILTER?.COL || [];
                    const initialState: Record<string, any> = {};

                    currentFilters.forEach((f: any) => {
                        // Skip the 'state' filter (used for view selection tracking)
                        if (f.name === "state") return;

                        const normalizedName =
                            normalizeFilterName(f.name) || f.name;
                        const key = `${normalizedName}_${f.type || "equals"}`;
                        initialState[key] = {
                            name: normalizedName,
                            type: f.type || "equals",
                            value: intentCurrentUserNames.has(normalizedName)
                                ? CURRENT_USER_INTENT_TOKEN
                                : f.value || "",
                            negation: f.negation || false,
                        };
                    });

                    initialFilterStateRef.current = initialState;
                    lastCapturedViewIdRef.current = selectedViewId;
                    isApplyingViewRef.current = false;
                    prevFilterParamsForStabilizationRef.current = null;
                    baselineCaptureTimeoutRef.current = null;
                    
                    // Reset hasFilterChanges after baseline is captured
                    setHasFilterChanges(false);
                } catch (error) {
                    console.error("Error storing initial filter state:", error);
                    initialFilterStateRef.current = {};
                    lastCapturedViewIdRef.current = null;
                    isApplyingViewRef.current = false;
                }
            }, 200); // Wait 200ms for filterParams to stabilize

            return () => {
                if (baselineCaptureTimeoutRef.current) {
                    clearTimeout(baselineCaptureTimeoutRef.current);
                    baselineCaptureTimeoutRef.current = null;
                }
            };
        } else {
            // Already stabilized, capture immediately
            if (baselineCaptureTimeoutRef.current) {
                clearTimeout(baselineCaptureTimeoutRef.current);
                baselineCaptureTimeoutRef.current = null;
            }

            try {
                const intentCurrentUserNames =
                    getIntentCurrentUserFilterNames(selectedView);
                const currentFilters = filterParams.FILTER?.COL || [];
                const initialState: Record<string, any> = {};

                currentFilters.forEach((f: any) => {
                    if (f.name === "state") return;

                    const normalizedName = normalizeFilterName(f.name) || f.name;
                    const key = `${normalizedName}_${f.type || "equals"}`;
                    initialState[key] = {
                        name: normalizedName,
                        type: f.type || "equals",
                        value: intentCurrentUserNames.has(normalizedName)
                            ? CURRENT_USER_INTENT_TOKEN
                            : f.value || "",
                        negation: f.negation || false,
                    };
                });

                initialFilterStateRef.current = initialState;
                lastCapturedViewIdRef.current = selectedViewId;
                isApplyingViewRef.current = false;
                prevFilterParamsForStabilizationRef.current = null;
                
                // Reset hasFilterChanges after baseline is captured
                setHasFilterChanges(false);
            } catch (error) {
                console.error("Error storing initial filter state:", error);
                initialFilterStateRef.current = {};
                lastCapturedViewIdRef.current = null;
                isApplyingViewRef.current = false;
            }
        }
        // Only depend on selectedViewId, filterParams, and reorderedViews
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedViewId, filterParams, reorderedViews]);

    // Deep equality comparison function for filter states
    const deepEqualFilters = useCallback(
        (initial: Record<string, any>, current: Record<string, any>): boolean => {
            const initialKeys = Object.keys(initial).filter(
                (k) => initial[k]?.name !== "state"
            );
            const currentKeys = Object.keys(current).filter(
                (k) => current[k]?.name !== "state"
            );

            if (initialKeys.length !== currentKeys.length) {
                return false;
            }

            for (const key of initialKeys) {
                const init = initial[key];
                const curr = current[key];

                if (!curr) {
                    return false; // Filter removed
                }

                // Normalize values for comparison (handle null, undefined, empty strings)
                const initValue = String(init?.value ?? "").trim();
                const currValue = String(curr?.value ?? "").trim();

                if (initValue !== currValue) {
                    return false;
                }

                // Compare filter type (normalize to "equals" if missing)
                if ((init?.type || "equals") !== (curr?.type || "equals")) {
                    return false;
                }

                // Compare negation flag (normalize to boolean)
                if (!!init?.negation !== !!curr?.negation) {
                    return false;
                }
            }

            // Check for new filters added (exist in current but not in initial)
            for (const key of currentKeys) {
                if (!initial[key]) {
                    return false; // New filter added
                }
            }

            return true;
        },
        []
    );

    // Check for filter changes - only runs when not in initial application phase
    const checkForChanges = useCallback(() => {
        if (!selectedViewId || !filterParams) {
            setHasFilterChanges(false);
            return;
        }

        // Skip comparison if we're still applying the view
        if (isApplyingViewRef.current) {
            return;
        }

        // Skip if baseline hasn't been captured yet
        if (lastCapturedViewIdRef.current !== selectedViewId) {
            return;
        }

        const selectedView = reorderedViews.find(
            (v) => v.id === selectedViewId
        );
        if (!selectedView) {
            setHasFilterChanges(false);
            return;
        }

        try {
            // Extract current filters from filterParams
            const currentFilters = filterParams.FILTER?.COL || [];
            const initialFilters = initialFilterStateRef.current;
            const intentCurrentUserNames =
                getIntentCurrentUserFilterNames(selectedView);

            // Normalize current filters for comparison
            const normalizedCurrent: Record<string, any> = {};
            currentFilters.forEach((f: any) => {
                // Skip the 'state' filter
                if (f.name === "state") return;

                const normalizedName = normalizeFilterName(f.name) || f.name;
                const key = `${normalizedName}_${f.type || "equals"}`;
                normalizedCurrent[key] = {
                    name: normalizedName,
                    type: f.type || "equals",
                    value: normalizeValueForIntentComparison(
                        normalizedName,
                        f.value,
                        intentCurrentUserNames
                    ),
                    negation: f.negation || false,
                };
            });

            // Use deep equality comparison
            const hasChanges = !deepEqualFilters(initialFilters, normalizedCurrent);

            setHasFilterChanges(hasChanges);

            // Save filter overrides when user has made changes (runtime persistence)
            if (hasChanges && selectedViewId) {
                const filtersToSave = currentFilters.filter(
                    (f: any) => f.name !== "state"
                );
                if (filtersToSave.length > 0) {
                    filterOverridesRef.current[selectedViewId] = filtersToSave;
                } else {
                    // Remove from storage if no filters to persist
                    delete filterOverridesRef.current[selectedViewId];
                }
            }
        } catch (error) {
            console.error("Error comparing filters:", error);
            setHasFilterChanges(false);
        }
    }, [selectedViewId, filterParams, reorderedViews, deepEqualFilters]);

    // Detect filter changes - only when not in initial application phase
    // This effect runs when filterParams changes, but only checks if we're not applying a view
    useEffect(() => {
        // Only check if we're not in the initial application phase
        if (isApplyingViewRef.current) {
            return;
        }

        checkForChanges();
    }, [filterParams, checkForChanges]);

    // Reset baseline function - re-captures baseline from current filterParams
    const resetBaseline = useCallback(() => {
        if (!selectedViewId || !filterParams) {
            return;
        }

        try {
            const currentFilters = filterParams.FILTER?.COL || [];
            const newBaseline: Record<string, any> = {};
            const selectedView = reorderedViews.find(
                (v) => v.id === selectedViewId
            );
            const intentCurrentUserNames =
                getIntentCurrentUserFilterNames(selectedView);

            currentFilters.forEach((f: any) => {
                // Skip the 'state' filter (used for view selection tracking)
                if (f.name === "state") return;

                const normalizedName = normalizeFilterName(f.name) || f.name;
                const key = `${normalizedName}_${f.type || "equals"}`;
                newBaseline[key] = {
                    name: normalizedName,
                    type: f.type || "equals",
                    value: intentCurrentUserNames.has(normalizedName)
                        ? CURRENT_USER_INTENT_TOKEN
                        : f.value || "",
                    negation: f.negation || false,
                };
            });

            initialFilterStateRef.current = newBaseline;
            lastCapturedViewIdRef.current = selectedViewId;
            setHasFilterChanges(false);
            
            // Clear any filter overrides since we're resetting to baseline
            delete filterOverridesRef.current[selectedViewId];
        } catch (error) {
            console.error("Error resetting baseline:", error);
        }
    }, [
        selectedViewId,
        filterParams,
        reorderedViews,
        getIntentCurrentUserFilterNames,
        normalizeFilterName,
    ]);

    // Expose function to get persisted filters for a view
    useEffect(() => {
        if (getPersistedFiltersRef) {
            getPersistedFiltersRef.current = (viewId: string): any[] | null => {
                return filterOverridesRef.current[viewId] || null;
            };
        }
    }, [getPersistedFiltersRef]);

    // Expose resetBaseline function via ref
    useEffect(() => {
        if (resetBaselineRef) {
            resetBaselineRef.current = resetBaseline;
        }
    }, [resetBaselineRef, resetBaseline]);

    // Re-select view after views list updates (for edit save)
    useEffect(() => {
        if (pendingReselectViewIdRef.current) {
            const viewId = pendingReselectViewIdRef.current;
            const updatedView = reorderedViews.find((v) => v.id === viewId);
            if (updatedView) {
                pendingReselectViewIdRef.current = null;
                onViewSelect(updatedView);
            }
        }
    }, [reorderedViews, onViewSelect]);

    const handleLoadMore = useCallback(() => {
        if (isViewsExpanded) {
            // Show less - collapse back to initial count
            setDisplayCount(5);
            setIsViewsExpanded(false);
        } else {
            // Show more - expand to show more views
            const newCount = displayCount + 20;
            setDisplayCount(newCount);
            setIsViewsExpanded(true);

            // If we've displayed all current views and there are more, call onLoadMore
            if (onLoadMore && newCount > reorderedViews.length && hasMore) {
                onLoadMore();
            }
        }
    }, [
        displayCount,
        reorderedViews.length,
        hasMore,
        onLoadMore,
        isViewsExpanded,
    ]);

    const handleCreateView = useCallback(() => {
        if (isMobile && onClose) {
            onClose();
        }
        if (onCreateView) {
            onCreateView();
        } else {
            setIsCreateDialogOpen(true);
        }
    }, [onCreateView, isMobile, onClose]);

    const handleCreateViewSave = useCallback(
        async (data: any) => {
            // View is created, close dialog and refresh views list
            setIsCreateDialogOpen(false);
            setDisplayCount(5); // Reset display count when refreshing

            // Call external refresh callback
            if (onViewsRefresh) {
                onViewsRefresh();
            }
        },
        [onViewsRefresh]
    );

    const handleCreateViewCancel = useCallback(() => {
        setIsCreateDialogOpen(false);
    }, []);

    const handleEditView = useCallback(
        (view: CustomView) => {
            if (isMobile && onClose) {
                onClose();
            }
            setEditingView(view);
            setIsEditDialogOpen(true);
        },
        [isMobile, onClose]
    );

    const handleEditViewSave = useCallback(
        async (viewData: ICustomViewData) => {
            const editedViewId = editingView?.id;
            const wasSelected = selectedViewId === editedViewId;

            if (!editedViewId || !editingView) {
                setIsEditDialogOpen(false);
                setEditingView(null);
                return;
            }

            // Reconstruct the CustomView object from viewData
            const name = transformNamesToName(viewData.columnsSetup.names);
            const definition = buildDefinition(viewData);

            const updatedCustomView: CustomView = {
                ...editingView,
                NAME: name,
                definition: definition,
                level: viewData.columnsSetup.viewAccess,
                ...(viewData.columnsSetup.viewAccess === "profile" &&
                viewData.columnsSetup.selectedProfileId
                    ? { assignTo: viewData.columnsSetup.selectedProfileId }
                    : {}),
            };

            // Update the view in reorderedViews state in-place
            setReorderedViews((prevViews) => {
                const updatedViews = prevViews.map((view) =>
                    view.id === editedViewId ? updatedCustomView : view
                );
                return updatedViews;
            });

            setIsEditDialogOpen(false);
            setEditingView(null);

            // If the edited view was selected, schedule re-selection after refresh
            if (wasSelected && editedViewId) {
                pendingReselectViewIdRef.current = editedViewId;
            }

            // Refresh views list - the useEffect will re-select when views update
            if (onViewsRefresh) {
                await onViewsRefresh();
            }

            // If the edited view was selected, reset baseline after view is re-selected
            // This ensures the baseline matches the updated view definition
            // Use a delay to ensure the view has been re-selected and filterParams updated
            if (wasSelected && editedViewId) {
                // Force re-capture of baseline by clearing lastCapturedViewIdRef
                // This will trigger the baseline capture logic when the view is re-selected
                lastCapturedViewIdRef.current = null;
                // Also reset the applying flag to allow baseline capture
                isApplyingViewRef.current = false;
            }

            // Ensure the views list is updated by resetting display count
            setDisplayCount(5);
            setIsViewsExpanded(false);
        },
        [onViewsRefresh, editingView, selectedViewId]
    );

    const handleEditViewCancel = useCallback(() => {
        setIsEditDialogOpen(false);
        setEditingView(null);
    }, []);

    const handleDeleteViewClick = useCallback((view: CustomView) => {
        setViewToDelete(view);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!viewToDelete || !viewToDelete.id) return;

        const wasSelected = selectedViewId === viewToDelete.id;

        setIsDeleting(true);
        try {
            const response = await featureService.deleteCustomViews(
                new DeleteCustomViewsRequest(
                    depStore.Server,
                    depStore.SessionId,
                    [{ id: viewToDelete.id }]
                )
            );
            if (response?.result === "OK") {
                // If the deleted view was selected, reset to initial state
                if (wasSelected && onViewSelect) {
                    onViewSelect(null);
                }
                if (onViewsRefresh) {
                    onViewsRefresh();
                }
            }
        } catch (error) {
            console.error("Error deleting custom view:", error);
        } finally {
            setIsDeleting(false);
            setViewToDelete(null);
        }
    }, [
        viewToDelete,
        selectedViewId,
        featureService,
        depStore.Server,
        depStore.SessionId,
        onViewsRefresh,
        onViewSelect,
    ]);

    const handleDeleteCancel = useCallback(() => {
        setViewToDelete(null);
    }, []);

    const handleEditModeToggle = useCallback(async () => {
        const wasInEditMode = isEditMode;
        setIsEditMode((prev) => !prev);

        // Reset drag states when exiting edit mode
        if (wasInEditMode) {
            setDraggedKey(null);
            setDragOverKey(null);

            // Save sort order when exiting edit mode
            if (!reorderedViews.length) return;

            // Check if order has changed
            const currentOrder = reorderedViews
                .map((v) => v.id || "")
                .filter((id) => id);
            const initialOrder = initialViewsOrderRef.current.filter(
                (id) => id
            );

            if (
                currentOrder.length !== initialOrder.length ||
                currentOrder.some((id, index) => id !== initialOrder[index])
            ) {
                // Order has changed, update priorities
                try {
                    // Group views by level
                    const orgViews = reorderedViews.filter(
                        (v) => v.level === "organization"
                    );
                    const profileViews = reorderedViews.filter(
                        (v) => v.level === "profile"
                    );
                    const userViews = reorderedViews.filter(
                        (v) => v.level === "user" || !v.level
                    );

                    // Calculate priorities maintaining order within each group
                    const viewsWithPriorities: CustomView[] = [
                        // Organization level: 0-1000
                        ...orgViews.map((v, i) => ({
                            ...v,
                            priority: String(Math.min(i, 1000)),
                        })),
                        // Profile level: 1001-10000
                        ...profileViews.map((v, i) => ({
                            ...v,
                            priority: String(Math.min(1001 + i, 10000)),
                        })),
                        // User level: 10000+
                        ...userViews.map((v, i) => ({
                            ...v,
                            priority: String(10000 + i),
                        })),
                    ];

                    // Update all views with new priorities
                    if (viewsWithPriorities.length > 0) {
                        await featureService.updateCustomViews(
                            new UpdateCustomViewsRequest(
                                depStore.Server,
                                depStore.SessionId,
                                viewsWithPriorities
                            )
                        );
                    }

                    // Update initial order reference
                    initialViewsOrderRef.current = currentOrder;
                } catch (error) {
                    console.error("Error saving sort order:", error);
                }
            }
        }
    }, [
        isEditMode,
        reorderedViews,
        featureService,
        depStore.Server,
        depStore.SessionId,
    ]);

    // Drag and drop handlers
    const handleDragStart = useCallback(
        (viewId: string) => {
            if (!isEditMode) return;
            setDraggedKey(viewId);
        },
        [isEditMode]
    );

    const handleDragOver = useCallback(
        (viewId: string, e: React.DragEvent) => {
            if (!isEditMode || !draggedKey || draggedKey === viewId) return;
            e.preventDefault();
            e.stopPropagation();
            setDragOverKey(viewId);
        },
        [isEditMode, draggedKey]
    );

    const handleDragEnd = useCallback(() => {
        // If drop already occurred (from onDrop), skip processing to prevent double call
        if (dropOccurredRef.current) {
            // Reset drag states
            setTimeout(() => {
                setDraggedKey(null);
                setDragOverKey(null);
                dropOccurredRef.current = false;
            }, 0);
            return;
        }

        if (!isEditMode || !draggedKey) {
            setDraggedKey(null);
            setDragOverKey(null);
            dropOccurredRef.current = false;
            return;
        }

        if (draggedKey && dragOverKey && draggedKey !== dragOverKey) {
            const draggedIndex = reorderedViews.findIndex(
                (v) => v.id === draggedKey
            );
            const dropIndex = reorderedViews.findIndex(
                (v) => v.id === dragOverKey
            );

            if (draggedIndex !== -1 && dropIndex !== -1) {
                // Reorder views
                const newViews = [...reorderedViews];
                const [removed] = newViews.splice(draggedIndex, 1);

                // Calculate the correct insertion index based on movement direction
                //
                // When moving up (draggedIndex > dropIndex):
                //   - User drags item from lower position to higher position (e.g., drag 5th item above 1st item)
                //   - When hovering "above" a target item, the dragOverKey may be set to the item below the hover position
                //   - To place the item at the correct position (before the visual target), we need to adjust
                //   - Insert at dropIndex - 1 to account for the hover detection offset
                //   - After removing from higher index, indices before removal point are unaffected
                //
                // When moving down (draggedIndex < dropIndex):
                //   - User drags item from higher position to lower position (e.g., drag 1st item to last position)
                //   - After removing the item, all items after draggedIndex shift left by 1
                //   - The target item that was at dropIndex is now at dropIndex - 1 in the new array
                //   - To place "at/after" the target, insert at dropIndex (original target position)
                //   - Special case: when dropIndex equals the last position, this equals newViews.length, placing at end
                let insertIndex: number;
                if (draggedIndex < dropIndex) {
                    // Moving down: insert at dropIndex (places at target position after accounting for removal shift)
                    insertIndex = dropIndex;
                } else {
                    // Moving up: adjust dropIndex by -1 to correct for hover detection offset
                    // When dragging "above" an item, the system may detect the item below as the target
                    // By subtracting 1, we place it before the detected item (which is where user intended)
                    insertIndex = Math.max(0, dropIndex);
                }

                // Ensure insertIndex is within bounds (splice allows inserting at array.length)
                const safeInsertIndex = Math.max(
                    0,
                    Math.min(insertIndex, newViews.length)
                );

                newViews.splice(safeInsertIndex, 0, removed);
                setReorderedViews(newViews);

                // Mark that drop occurred to prevent subsequent handleDragEnd from processing
                dropOccurredRef.current = true;
            }
        }

        // Reset drag states - use setTimeout to ensure hover state resets properly
        setTimeout(() => {
            setDraggedKey(null);
            setDragOverKey(null);
            dropOccurredRef.current = false;
        }, 0);
    }, [isEditMode, draggedKey, dragOverKey, reorderedViews]);

    const handleDragLeave = useCallback(() => {
        setDragOverKey(null);
    }, []);

    // Touch drag handlers for mobile/tablet
    const handleTouchStart = useCallback(
        (e: React.TouchEvent, viewId: string) => {
            const isTablet = deviceInfo?.isTablet || deviceInfo?.isIPad;
            if (!isEditMode || !viewId || !(isMobile || isTablet)) return;

            const touch = e.touches[0];
            if (!touch) return;

            e.preventDefault();
            e.stopPropagation();

            touchDragRef.current = {
                viewId,
                startY: touch.clientY,
                currentY: touch.clientY,
            };
            isTouchDraggingRef.current = true;
            setDraggedKey(viewId);
        },
        [isEditMode, isMobile, deviceInfo]
    );

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!isTouchDraggingRef.current || !touchDragRef.current) return;

        const touch = e.touches[0];
        if (!touch) return;

        e.preventDefault();
        e.stopPropagation();

        touchDragRef.current.currentY = touch.clientY;

        // Find element under touch point
        const elementUnderTouch = document.elementFromPoint(
            touch.clientX,
            touch.clientY
        );

        if (!elementUnderTouch) {
            setDragOverKey(null);
            return;
        }

        // Traverse up DOM tree to find view item wrapper with data-view-id
        let currentElement: HTMLElement | null =
            elementUnderTouch as HTMLElement;
        let targetViewId: string | null = null;

        while (currentElement && currentElement !== document.body) {
            const viewId = currentElement.getAttribute("data-view-id");
            if (viewId) {
                targetViewId = viewId;
                break;
            }
            currentElement = currentElement.parentElement;
        }

        // Update dragOverKey if we found a valid target
        if (targetViewId && targetViewId !== touchDragRef.current.viewId) {
            setDragOverKey(targetViewId);
        } else {
            setDragOverKey(null);
        }
    }, []);

    const handleTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            if (!isTouchDraggingRef.current || !touchDragRef.current) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            const touch = e.changedTouches[0];
            if (!touch) {
                // Reset state
                isTouchDraggingRef.current = false;
                touchDragRef.current = null;
                setDraggedKey(null);
                setDragOverKey(null);
                return;
            }

            // Find element under touch point
            const elementUnderTouch = document.elementFromPoint(
                touch.clientX,
                touch.clientY
            );

            if (elementUnderTouch) {
                // Traverse up to find view item wrapper
                let currentElement: HTMLElement | null =
                    elementUnderTouch as HTMLElement;

                while (currentElement) {
                    const viewId = currentElement.getAttribute("data-view-id");
                    if (viewId) {
                        const draggedViewId = touchDragRef.current.viewId;
                        if (viewId !== draggedViewId) {
                            setDragOverKey(viewId);
                            // Small delay to ensure state is set before calling handleDragEnd
                            setTimeout(() => {
                                handleDragEnd();
                            }, 0);
                        }
                        break;
                    }
                    currentElement = currentElement.parentElement;
                }
            }

            // Reset touch drag state
            setTimeout(() => {
                isTouchDraggingRef.current = false;
                touchDragRef.current = null;
                setDraggedKey(null);
                setDragOverKey(null);
            }, 100);
        },
        [handleDragEnd]
    );

    const handleDuplicateView = useCallback(
        (view: CustomView) => {
            if (isMobile && onClose) {
                onClose();
            }

            // Create a modified CustomView with "Copy of " prepended to name(s)
            const duplicateView: CustomView = {
                ...view,
                id: undefined, // Remove ID to make it a new view
                name: view.name ? `Copy of ${view.name}` : undefined,
                NAME: view.NAME
                    ? {
                          TRANSLATION:
                              view.NAME.TRANSLATION?.map((t) => ({
                                  ...t,
                                  text: `Copy of ${t.text || ""}`,
                              })) || [],
                      }
                    : undefined,
            };

            setDuplicateView(duplicateView);
            setIsDuplicateDialogOpen(true);
        },
        [isMobile, onClose]
    );

    const handleDuplicateViewSave = useCallback(
        async (data: any) => {
            // View is created, close dialog and refresh views list
            setIsDuplicateDialogOpen(false);
            setDuplicateView(null);
            setDisplayCount(5); // Reset display count when refreshing
            setIsViewsExpanded(false);

            // Call external refresh callback
            if (onViewsRefresh) {
                onViewsRefresh();
            }
        },
        [onViewsRefresh]
    );

    const handleDuplicateViewCancel = useCallback(() => {
        setIsDuplicateDialogOpen(false);
        setDuplicateView(null);
    }, []);

    const getViewDisplayName = (view: CustomView): string => {
        if (view.name) return view.name;
        if (view.NAME?.TRANSLATION && view.NAME.TRANSLATION.length > 0) {
            return view.NAME.TRANSLATION[0].text || "";
        }
        return "";
    };

    const getViewIcon = (
        view: CustomView
    ): { icon: React.ReactElement; className: string } => {
        const level = view.level?.toLowerCase();

        if (level === "organization") {
            return {
                icon: <Building size="S" />,
                className: styles.view_icon_organization,
            };
        } else if (level === "profile") {
            return {
                icon: <UserGroup size="S" />,
                className: styles.view_icon_profile,
            };
        } else if (level === "user") {
            return {
                icon: <User size="S" />,
                className: styles.view_icon_user,
            };
        } else {
            return {
                icon: <Alert size="S" />,
                className: styles.view_icon_default,
            };
        }
    };

    const isViewSelected = (viewId: string | undefined): boolean => {
        if (!selectedViewId) return false;
        return selectedViewId === viewId;
    };

    const handleCustomViewClick = useCallback(
        (view: CustomView) => {
            onViewSelect(view);
            if (isMobile && onClose) {
                onClose();
            }
        },
        [onViewSelect, isMobile, onClose]
    );

    const displayedViews = reorderedViews.slice(0, displayCount);
    const canLoadMore = hasMore || displayCount < reorderedViews.length;
    const canShowLess = isViewsExpanded && displayCount > 5;

    // Determine if we're on a tablet device
    const isTabletDevice = deviceInfo?.isTablet || deviceInfo?.isIPad;

    const renderViewItem = (
        index: number,
        viewId: string,
        displayName: string,
        isSelected: boolean,
        onClick: () => void,
        key?: string,
        customView?: CustomView
    ) => {
        const isDragging = draggedKey === viewId;
        const isDragOver = dragOverKey === viewId;
        const canDrag = isEditMode && customView;
        const level = customView?.level?.toLowerCase();

        return (
            <React.Fragment key={key}>
                {index === 5 && (
                    <View
                        width={"100%"}
                        height={"1px"}
                        UNSAFE_className={styles.separator}
                    />
                )}
                <div
                    style={{
                        width: "100%",
                        position: "relative",
                    }}
                    data-view-id={customView?.id || undefined}
                    className={clsx(styles.view_item_wrapper, {
                        [styles.view_item_wrapper_tablet]: isTabletDevice,
                        [styles.view_item_wrapper_edit_mode]:
                            isEditMode && customView,
                        [styles.view_item_dragging]: isDragging,
                        [styles.view_item_drag_over]: isDragOver,
                        [styles.view_item_wrapper_organization]:
                            level === "organization",
                        [styles.view_item_wrapper_profile]: level === "profile",
                        [styles.view_item_wrapper_user]:
                            level === "user" || (!level && customView),
                    })}
                    onDragOver={(e: React.DragEvent) => {
                        if (
                            canDrag &&
                            customView?.id &&
                            draggedKey &&
                            draggedKey !== customView.id
                        ) {
                            handleDragOver(customView.id, e);
                        }
                    }}
                    onDragLeave={handleDragLeave}
                    onDrop={(e: React.DragEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Call handleDragEnd - it will set dropOccurredRef to true after processing
                        // This prevents onDragEnd from processing the same drop
                        handleDragEnd();
                    }}
                    onTouchMove={
                        isMobile || isTabletDevice ? handleTouchMove : undefined
                    }
                >
                    <button
                        onClick={onClick}
                        style={{
                            cursor: "pointer",
                            width: "100%",
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            textAlign: "left",
                            position: "relative",
                            zIndex: 0,
                        }}
                    >
                        <Flex
                            alignSelf={isMobile ? "stretch" : undefined}
                            UNSAFE_className={
                                isSelected
                                    ? styles.filter_views_container_selected
                                    : styles.filter_views_container
                            }
                        >
                            <Flex
                                direction={"row"}
                                flex={1}
                                alignItems={"center"}
                                justifyContent={"start"}
                                alignSelf={isMobile ? "stretch" : undefined}
                                minWidth={0}
                                gap={"size-100"}
                            >
                                {canDrag && (
                                    <div
                                        className={styles.drag_handle}
                                        draggable={
                                            !(isMobile || isTabletDevice)
                                        }
                                        onDragStart={(e: React.DragEvent) => {
                                            if (customView?.id) {
                                                handleDragStart(customView.id);
                                                e.dataTransfer.effectAllowed =
                                                    "move";
                                                e.dataTransfer.setData(
                                                    "text/plain",
                                                    customView.id
                                                );
                                            }
                                        }}
                                        onDragEnd={handleDragEnd}
                                        onMouseDown={(e: React.MouseEvent) =>
                                            e.stopPropagation()
                                        }
                                        onPointerDown={(
                                            e: React.PointerEvent
                                        ) => e.stopPropagation()}
                                        onTouchStart={
                                            isMobile || isTabletDevice
                                                ? (e: React.TouchEvent) => {
                                                      if (customView?.id) {
                                                          handleTouchStart(
                                                              e,
                                                              customView.id
                                                          );
                                                      }
                                                  }
                                                : undefined
                                        }
                                        onTouchEnd={
                                            isMobile || isTabletDevice
                                                ? handleTouchEnd
                                                : undefined
                                        }
                                    >
                                        <Rail
                                            UNSAFE_style={{
                                                width: "26px",
                                                height: "26px",
                                            }}
                                        />
                                    </div>
                                )}
                                {customView &&
                                    isEditMode &&
                                    (() => {
                                        const { icon, className } =
                                            getViewIcon(customView);
                                        return (
                                            <Flex
                                                alignItems={"center"}
                                                justifyContent={"center"}
                                                UNSAFE_className={className}
                                            >
                                                {icon}
                                            </Flex>
                                        );
                                    })()}
                                <Flex
                                    position={"relative"}
                                    flex={1}
                                    UNSAFE_className={styles.filter_views_text}
                                    minWidth={0}
                                    alignItems={"center"}
                                    gap={"size-100"}
                                >
                                    {isSelected && hasFilterChanges && (
                                        <div
                                            className={
                                                styles.filter_change_indicator
                                            }
                                        />
                                    )}
                                    <span>{displayName}</span>
                                </Flex>
                            </Flex>
                        </Flex>
                    </button>
                    {customView && isEditMode && (
                        <div className={styles.view_item_actions}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditView(customView);
                                }}
                                className={styles.view_action_btn}
                                aria-label={t("edit", {
                                    ns: "layout_components",
                                    defaultValue: "Edit",
                                })}
                            >
                                <EditIcon size={14} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDuplicateView(customView);
                                }}
                                className={styles.view_action_btn}
                                aria-label={t("duplicate", {
                                    ns: "layout_components",
                                    defaultValue: "Duplicate",
                                })}
                            >
                                <CopyIcon size={14} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteViewClick(customView);
                                }}
                                className={styles.view_action_btn}
                                aria-label={t("delete", {
                                    ns: "layout_components",
                                    defaultValue: "Delete",
                                })}
                                disabled={isDeleting}
                            >
                                <TrashIcon size={14} />
                            </button>
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    };

    const viewsContent = (
        <Flex
            direction={"column"}
            alignSelf={"stretch"}
            alignItems={"start"}
            justifyContent={"start"}
            gap={"4px"}
            UNSAFE_className={styles.filter_views_parent}
        >
            {/* Custom Views */}
            {displayedViews.map((view, index) =>
                renderViewItem(
                    index,
                    view.id || `custom-view-${index}`,
                    getViewDisplayName(view),
                    isViewSelected(view.id),
                    () => handleCustomViewClick(view),
                    view.id || `custom-view-${index}`,
                    view
                )
            )}

            {/* Separator before Load More */}
            {canLoadMore && (
                <View
                    width={"100%"}
                    height={"1px"}
                    UNSAFE_className={styles.separator}
                />
            )}

            {/* Show More / Show Less Button */}
            {(canLoadMore || canShowLess) && (
                <button
                    onClick={handleLoadMore}
                    style={{
                        cursor: "pointer",
                        width: "100%",
                        maxWidth: "100%",
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        textAlign: "left",
                        boxSizing: "border-box",
                    }}
                >
                    <Flex
                        alignSelf={"stretch"}
                        UNSAFE_className={styles.load_more_container}
                    >
                        <Flex
                            direction={"row"}
                            flex={1}
                            alignItems={"center"}
                            justifyContent={"start"}
                            alignSelf={"stretch"}
                        >
                            <Flex
                                position={"relative"}
                                flex={1}
                                UNSAFE_className={styles.load_more_text}
                            >
                                {isViewsExpanded
                                    ? t("show_less", {
                                          ns: "layout_components",
                                          defaultValue: "Show Less",
                                      })
                                    : t("show_more", {
                                          ns: "layout_components",
                                          defaultValue: "Show More",
                                      })}
                            </Flex>
                        </Flex>
                    </Flex>
                </button>
            )}
        </Flex>
    );
    // Mobile/Tablet button components with icons
    const createButtonMobile = (
        <View UNSAFE_className={styles.mobile_view_button_wrapper}>
            <AtollonButton
                onPress={handleCreateView}
                designType={AtollonButtonDesignType.LINK}
            >
                <Flex direction="row" alignItems="center" gap="size-100">
                    <span>
                        {t("add", {
                            ns: "layout_components",
                            defaultValue: "Add",
                        })}
                    </span>
                </Flex>
            </AtollonButton>
        </View>
    );

    const editDoneButtonMobile = (
        <View UNSAFE_className={styles.mobile_view_button_wrapper}>
            <AtollonButton
                onPress={handleEditModeToggle}
                designType={AtollonButtonDesignType.LINK}
            >
                <Flex direction="row" alignItems="center" gap="size-100">
                    <span>
                        {isEditMode
                            ? t("done", {
                                  ns: "layout_components",
                                  defaultValue: "Done",
                              })
                            : t("edit", {
                                  ns: "layout_components",
                                  defaultValue: "Edit",
                              })}
                    </span>
                </Flex>
            </AtollonButton>
        </View>
    );

    // Desktop button components (unchanged)
    const createButton = (
        <Link isQuiet onPress={handleCreateView}>
            {t("add", { ns: "layout_components", defaultValue: "Add" })}
        </Link>
    );

    const editDoneButton = (
        <Link isQuiet onPress={handleEditModeToggle}>
            {isEditMode
                ? t("done", { ns: "layout_components", defaultValue: "Done" })
                : t("edit", { ns: "layout_components", defaultValue: "Edit" })}
        </Link>
    );

    if (isMobile) {
        return (
            <>
                <Offcanvas
                    show={show || false}
                    onHide={onClose}
                    placement="start"
                >
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title></Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body className={styles.offcanvas_body_mobile}>
                        <Flex
                            direction={"column"}
                            height={"100%"}
                            position={"relative"}
                            UNSAFE_style={{
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                            }}
                        >
                            <Flex
                                direction={"column"}
                                position={"relative"}
                                left={"15px"}
                                right={"15px"}
                                width={"calc(100% - 30px)"}
                                alignItems={"start"}
                                justifyContent={"start"}
                                gap={"5px"}
                                UNSAFE_className={styles.views_menu_container}
                                UNSAFE_style={{
                                    flex: "1 1 auto",
                                    overflowY: "auto",
                                    paddingTop: "16px",
                                    paddingBottom: "16px",
                                }}
                            >
                                <Flex
                                    direction={"row"}
                                    UNSAFE_className={styles.views_menu_heading}
                                    alignSelf={"stretch"}
                                    alignItems={"center"}
                                    justifyContent={"start"}
                                >
                                    <View
                                        position={"relative"}
                                        UNSAFE_className={
                                            styles.menu_heading_text
                                        }
                                    >
                                        {t("views", {
                                            ns: "layout_components",
                                        })}
                                    </View>
                                </Flex>
                                {viewsContent}
                            </Flex>
                            <View
                                UNSAFE_className={
                                    styles.mobile_buttons_container
                                }
                            >
                                <Flex
                                    direction={"column"}
                                    width={"100%"}
                                    gap={"size-150"}
                                >
                                    {createButtonMobile}
                                    {editDoneButtonMobile}
                                </Flex>
                            </View>
                        </Flex>
                    </Offcanvas.Body>
                </Offcanvas>

                <DialogContainer
                    isDismissable={false}
                    onDismiss={handleCreateViewCancel}
                >
                    {isCreateDialogOpen && (
                        <CustomViewDialog
                            formSettings={formSettings || []}
                            applicationId={selectedAppId || ""}
                            onCancel={handleCreateViewCancel}
                            onSave={handleCreateViewSave}
                        />
                    )}
                </DialogContainer>

                <DialogContainer
                    isDismissable={false}
                    onDismiss={handleEditViewCancel}
                >
                    {isEditDialogOpen && editingView && (
                        <CustomViewDialog
                            formSettings={formSettings || []}
                            applicationId={selectedAppId || ""}
                            onCancel={handleEditViewCancel}
                            onSave={handleEditViewSave}
                            editView={editingView}
                        />
                    )}
                </DialogContainer>

                <DialogContainer onDismiss={handleDeleteCancel}>
                    {viewToDelete && (
                        <AlertDialog
                            title={t("delete_view", {
                                ns: "layout_components",
                                defaultValue: "Delete View",
                            })}
                            variant="destructive"
                            primaryActionLabel={t("delete", {
                                ns: "layout_components",
                                defaultValue: "Delete",
                            })}
                            cancelLabel={t("cancel", {
                                ns: "layout_components",
                                defaultValue: "Cancel",
                            })}
                            onPrimaryAction={handleDeleteConfirm}
                            onCancel={handleDeleteCancel}
                            isPrimaryActionDisabled={isDeleting}
                        >
                            {t("delete_view_confirmation", {
                                ns: "layout_components",
                                defaultValue:
                                    "Are you sure you want to delete this view? This action cannot be undone.",
                            })}
                        </AlertDialog>
                    )}
                </DialogContainer>

                <DialogContainer
                    isDismissable={false}
                    onDismiss={handleDuplicateViewCancel}
                >
                    {isDuplicateDialogOpen && duplicateView && (
                        <CustomViewDialog
                            formSettings={formSettings || []}
                            applicationId={selectedAppId || ""}
                            onCancel={handleDuplicateViewCancel}
                            onSave={handleDuplicateViewSave}
                            editView={duplicateView}
                            isDuplicate={true}
                        />
                    )}
                </DialogContainer>
            </>
        );
    }

    return (
        <>
            <Flex
                direction={"column"}
                width={"100%"}
                alignItems={"stretch"}
                justifyContent={"start"}
                gap={"12px"}
                UNSAFE_className={styles.sidebar_container}
            >
                <Flex
                    direction={"column"}
                    gap={"5px"}
                    UNSAFE_className={styles.views_menu_container}
                >
                    <Flex
                        direction={"row"}
                        UNSAFE_className={styles.views_menu_heading}
                        alignSelf={"stretch"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <View
                            position={"relative"}
                            UNSAFE_className={styles.menu_heading_text}
                        >
                            {t("views", { ns: "layout_components" })}
                        </View>
                        <Flex
                            direction={"row"}
                            gap={"size-100"}
                            alignItems={"center"}
                        >
                            {editDoneButton}
                            <View UNSAFE_style={{ color: "#8f9fa6" }}>|</View>
                            {createButton}
                        </Flex>
                    </Flex>
                    {viewsContent}
                </Flex>
            </Flex>

            <DialogContainer
                isDismissable={false}
                onDismiss={handleCreateViewCancel}
            >
                {isCreateDialogOpen && (
                    <CustomViewDialog
                        formSettings={formSettings || []}
                        applicationId={selectedAppId || ""}
                        onCancel={handleCreateViewCancel}
                        onSave={handleCreateViewSave}
                    />
                )}
            </DialogContainer>

            <DialogContainer
                isDismissable={false}
                onDismiss={handleEditViewCancel}
            >
                {isEditDialogOpen && editingView && (
                    <CustomViewDialog
                        formSettings={formSettings || []}
                        applicationId={selectedAppId || ""}
                        onCancel={handleEditViewCancel}
                        onSave={handleEditViewSave}
                        editView={editingView}
                    />
                )}
            </DialogContainer>

            <DialogContainer onDismiss={handleDeleteCancel}>
                {viewToDelete && (
                    <AlertDialog
                        title={t("delete_view", {
                            ns: "layout_components",
                            defaultValue: "Delete View",
                        })}
                        variant="destructive"
                        primaryActionLabel={t("delete", {
                            ns: "layout_components",
                            defaultValue: "Delete",
                        })}
                        cancelLabel={t("cancel", {
                            ns: "layout_components",
                            defaultValue: "Cancel",
                        })}
                        onPrimaryAction={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                        isPrimaryActionDisabled={isDeleting}
                    >
                        {t("delete_view_confirmation", {
                            ns: "layout_components",
                            defaultValue:
                                "Are you sure you want to delete this view? This action cannot be undone.",
                        })}
                    </AlertDialog>
                )}
            </DialogContainer>

            <DialogContainer
                isDismissable={false}
                onDismiss={handleDuplicateViewCancel}
            >
                {isDuplicateDialogOpen && duplicateView && (
                    <CustomViewDialog
                        formSettings={formSettings || []}
                        applicationId={selectedAppId || ""}
                        onCancel={handleDuplicateViewCancel}
                        onSave={handleDuplicateViewSave}
                        editView={duplicateView}
                        isDuplicate={true}
                    />
                )}
            </DialogContainer>
        </>
    );
};

export default FilterViewsComponent;
